import { useEffect, useState } from 'react';
import { api } from '../../utils/api';

// Frontend mirror of the backend payroll math (server/src/services/ssoCalc.ts + pnd1Calc.ts) so the
// employee table can show each person's monthly SSO contribution and PND.1 income-tax withholding LIVE as
// you type the salary. Rates / brackets / deductions are loaded once from the same config the Tax module
// uses (/sso/config/current + /pnd1/config/current), so these columns stay in lockstep with what the SSO /
// PND.1 modules actually file. (Per-employee personal allowances are not applied here — this is the
// standard-deduction estimate; the PND.1 module remains the source of truth for the filed amount.)
export interface Bracket { upTo: number | null; rate: number; }
export interface Deduction { name: string; amount: number; }
export interface Pnd1Config { expenseRate: number; expenseCap: number; ssoCap: number; brackets: Bracket[]; deductions: Deduction[]; }
export interface SsoConfig { rate: number; cap: number; floor: number; }
export interface PayrollConfig { sso: SsoConfig; pnd1: Pnd1Config; }

// One side's monthly SSO contribution. Wage 0 → 0 (don't fabricate). Else round(rate% of max(wage, floor)), capped.
export function ssoAmount(wage: number, cfg: SsoConfig): number {
  const w = Number(wage) || 0;
  if (w <= 0) return 0;
  const base = Math.max(w, cfg.floor);
  return Math.min(Math.round(base * cfg.rate / 100), cfg.cap);
}

function progressiveTax(net: number, brackets: Bracket[]): number {
  let tax = 0, lower = 0;
  for (const b of brackets) {
    const upper = b.upTo == null ? Infinity : b.upTo;
    if (net <= lower) break;
    tax += (Math.min(net, upper) - lower) * (b.rate || 0) / 100;
    lower = upper;
  }
  return tax;
}

// Monthly PND.1 withholding for one wage (standard-deduction method) — mirrors pnd1Withholding().
export function pnd1Monthly(wage: number, sso: SsoConfig, pnd1: Pnd1Config): number {
  const w = Number(wage) || 0;
  if (w <= 0) return 0;
  const annual = w * 12;
  const expense = Math.min(annual * pnd1.expenseRate / 100, pnd1.expenseCap);
  const allowances = (pnd1.deductions || []).reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const ssoPaid = Math.min(ssoAmount(w, sso) * 12, pnd1.ssoCap);
  const net = Math.max(0, annual - expense - allowances - ssoPaid);
  const annualTax = progressiveTax(net, pnd1.brackets);
  return Math.round(annualTax / 12 * 100) / 100;
}

// Load the two configs once and cache for the session.
let cache: Promise<PayrollConfig> | null = null;
export function loadPayrollConfig(): Promise<PayrollConfig> {
  if (!cache) cache = api.getPayrollConfig().then((c: any) => ({ sso: c.sso, pnd1: c.pnd1 } as PayrollConfig));
  return cache;
}
export function usePayrollConfig(): PayrollConfig | null {
  const [cfg, setCfg] = useState<PayrollConfig | null>(null);
  useEffect(() => { let alive = true; loadPayrollConfig().then(c => { if (alive) setCfg(c); }).catch(() => { /* leave null → columns show "…" */ }); return () => { alive = false; }; }, []);
  return cfg;
}

export const fmtBaht = (n: number): string => (n ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0');
